import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, Loader2, AlertCircle,
  ImagePlus, Save, X, Eye, EyeOff, FolderOpen, Pencil,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import NewsletterAdminNav from '../components/NewsletterAdminNav';
import { LT } from '../lib/appCatalogue';
import { useLocale, useT } from '../contexts/LocaleProvider';
import api from '../utils/api';

/* MANEVA photos · album-grouped CRUD. Two levels of visibility:
 *
 *   1. Album-level toggle (`is_active`) hides every photo in the
 *      album from the homepage carousel in one click.
 *   2. Per-photo toggle (`show_on_splash`) hides a single photo
 *      without touching its siblings.
 *
 * Carousel filter applied server-side: photo visible iff
 *   (album is null OR album.is_active) AND photo.show_on_splash.
 *
 * Page layout:
 *   - "New album" button at the top
 *   - One section per album, ordered by album.sort_order
 *   - "Unassigned" section at the bottom for photos with no album
 *   - Each section has its own "Add photo" button targeting that album
 */

export default function AdminNewsletterPhotos() {
  const [allowed, setAllowed] = useState(undefined);
  useEffect(() => {
    api.canEditContent().then(setAllowed).catch(() => setAllowed(false));
  }, []);
  if (allowed === undefined) return <Shell><Loading /></Shell>;
  if (!allowed) return <Shell><NotAllowed /></Shell>;
  return <PhotosAdmin />;
}

const Loading = () => (
  <div className="flex items-center gap-2 mt-10 text-[13px]" style={{ color: LT.green50 }}>
    <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
  </div>
);

const NotAllowed = () => {
  const t = useT();
  return (
    <div className="max-w-[520px] mt-10 rounded-2xl p-6"
         style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
      {t('photos.admin.notAllowed')}
    </div>
  );
};

const PhotosAdmin = () => {
  const t = useT();
  const [albums, setAlbums] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [addingPhotoTo, setAddingPhotoTo] = useState(null);   // album id, or 'none', or null

  const load = async () => {
    const [a, p] = await Promise.all([
      api.newsletterAlbums().then(r => Array.isArray(r) ? r : (r?.results || [])),
      api.newsletterPhotos().then(r => Array.isArray(r) ? r : (r?.results || [])),
    ]);
    setAlbums(a);
    setPhotos(p);
  };
  useEffect(() => { load().catch(() => { setAlbums([]); setPhotos([]); }); }, []);

  const photosByAlbum = useMemo(() => {
    const m = new Map();
    (photos || []).forEach(p => {
      const key = p.album ?? 'none';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(p);
    });
    return m;
  }, [photos]);

  const loading = albums === null || photos === null;
  const empty = !loading && (albums.length === 0 && photos.length === 0);

  return (
    <Shell>
      <NewsletterAdminNav />
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[2rem] sm:text-[2.4rem] leading-tight"
              style={{ color: LT.greenDeep }}>
            {t('photos.admin.title')}
          </h1>
          <p className="mt-2 text-[14px] max-w-[640px]" style={{ color: '#3a4f44' }}>
            {t('photos.admin.intro')}
          </p>
        </div>
        <button onClick={() => setCreatingAlbum(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium transition"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          <Plus className="h-4 w-4" /> {t('photos.admin.newAlbum')}
        </button>
      </header>

      {creatingAlbum && (
        <AlbumForm t={t}
                   onCancel={() => setCreatingAlbum(false)}
                   onSaved={() => { setCreatingAlbum(false); load(); }} />
      )}

      <SpotlightPanel t={t} />

      <section className="mt-2 space-y-6">
        {loading && <Loading />}
        {empty && (
          <div className="rounded-2xl p-6 text-[14px]"
               style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
            {t('photos.admin.empty')}
          </div>
        )}

        {albums?.map(al => (
          <AlbumSection
            key={al.id}
            t={t}
            album={al}
            photos={photosByAlbum.get(al.id) || []}
            editing={editingAlbum === al.id}
            addingPhoto={addingPhotoTo === al.id}
            onEdit={() => setEditingAlbum(al.id)}
            onCancelEdit={() => setEditingAlbum(null)}
            onAddPhoto={() => setAddingPhotoTo(al.id)}
            onCancelAddPhoto={() => setAddingPhotoTo(null)}
            onReload={load}
          />
        ))}

        {(photosByAlbum.get('none') || []).length > 0 && (
          <UnassignedSection
            t={t}
            photos={photosByAlbum.get('none') || []}
            addingPhoto={addingPhotoTo === 'none'}
            onAddPhoto={() => setAddingPhotoTo('none')}
            onCancelAddPhoto={() => setAddingPhotoTo(null)}
            onReload={load}
            albums={albums || []}
          />
        )}

        {/* If no unassigned photos exist yet, surface an "Add to no album" CTA */}
        {!loading && !(photosByAlbum.get('none') || []).length && (
          <button onClick={() => setAddingPhotoTo('none')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px]"
                  style={{ color: LT.green50, border: `1px dashed ${LT.green30}` }}>
            <Plus className="h-3.5 w-3.5" /> {t('photos.admin.addPhoto')} ({t('photos.admin.unassigned').toLowerCase()})
          </button>
        )}
      </section>

      <style>{`
        .ph-input { background:#fff; border:1px solid ${LT.green30}; border-radius:0.6rem;
                    padding:0.5rem 0.75rem; font-size:13.5px; color:#2b4138; width:100%; }
        .ph-input:focus { outline:none; border-color:${LT.greenDeep};
                          box-shadow:0 0 0 3px rgba(43,65,56,0.08); }
        .ph-textarea { background:#fff; border:1px solid ${LT.green30}; border-radius:0.6rem;
                       padding:0.5rem 0.75rem; font-size:13.5px; color:#2b4138; width:100%;
                       font-family:inherit; line-height:1.55; }
      `}</style>
    </Shell>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Album section · header (toggle + edit/delete) + photo grid.
 * ──────────────────────────────────────────────────────────────── */
const AlbumSection = ({
  t, album, photos, editing, addingPhoto,
  onEdit, onCancelEdit, onAddPhoto, onCancelAddPhoto, onReload,
}) => {
  const [open, setOpen] = useState(true);
  const [toggling, setToggling] = useState(false);

  const toggleActive = async () => {
    setToggling(true);
    try {
      await api.adminUpdateAlbum(album.id, { is_active: !album.is_active });
      onReload();
    } finally { setToggling(false); }
  };

  const remove = async () => {
    if (!confirm(t('photos.admin.deleteAlbumConfirm'))) return;
    await api.adminDeleteAlbum(album.id);
    onReload();
  };

  if (editing) {
    return (
      <AlbumForm t={t} album={album}
                 onCancel={onCancelEdit}
                 onSaved={() => { onCancelEdit(); onReload(); }} />
    );
  }

  return (
    <section className="rounded-2xl"
             style={{
               background: album.is_active ? LT.beigeLight : 'rgba(43,65,56,0.03)',
               border: `1px solid ${LT.green30}`,
               opacity: album.is_active ? 1 : 0.86,
             }}>
      <header className="flex items-center gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen(o => !o)}
                className="shrink-0"
                style={{ color: LT.green50 }} aria-label="toggle">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <FolderOpen className="h-4 w-4 shrink-0" style={{ color: LT.greenDeep }} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-[1.05rem] leading-tight truncate" style={{ color: LT.greenDeep }}>
            {album.name}
          </div>
          {album.description && (
            <div className="text-[12px] leading-tight mt-0.5 truncate" style={{ color: LT.green50 }}>
              {album.description}
            </div>
          )}
        </div>
        <span className="text-[10.5px] font-num uppercase tracking-[0.12em] shrink-0"
              style={{ color: LT.green50 }}>
          {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
        </span>
        <button type="button" onClick={toggleActive} disabled={toggling}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium"
                style={{
                  background: album.is_active ? '#e5efe7' : 'rgba(43,65,56,0.06)',
                  color: album.is_active ? '#3a6f4a' : LT.green50,
                }}>
          {album.is_active
            ? <><Eye className="h-3 w-3" /> {t('photos.admin.albumActive')}</>
            : <><EyeOff className="h-3 w-3" /> {t('photos.admin.albumHidden')}</>}
        </button>
        <button type="button" onClick={onEdit}
                className="p-1.5 rounded-md text-[11px]"
                style={{ color: LT.green50 }} title={t('photos.admin.editAlbum')}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={remove}
                className="p-1.5 rounded-md text-[11px]"
                style={{ color: '#b54338' }} title={t('photos.admin.deleteAlbum')}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </header>

      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: LT.green30 }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {photos.map(p => (
              <PhotoCard key={p.id} t={t} photo={p} onReload={onReload} />
            ))}
          </div>
          {!addingPhoto ? (
            <button onClick={onAddPhoto}
                    className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              <Plus className="h-3.5 w-3.5" /> {t('photos.admin.addPhoto')}
            </button>
          ) : (
            <div className="mt-4">
              <PhotoForm t={t} albumId={album.id}
                         onCancel={onCancelAddPhoto}
                         onSaved={() => { onCancelAddPhoto(); onReload(); }} />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const UnassignedSection = ({ t, photos, addingPhoto, onAddPhoto, onCancelAddPhoto, onReload, albums }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="rounded-2xl"
             style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}` }}>
      <header className="flex items-center gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen(o => !o)}
                className="shrink-0" style={{ color: LT.green50 }}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[1rem] leading-tight" style={{ color: LT.greenDeep }}>
            {t('photos.admin.unassigned')}
          </div>
        </div>
        <span className="text-[10.5px] font-num uppercase tracking-[0.12em] shrink-0"
              style={{ color: LT.green50 }}>
          {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
        </span>
      </header>
      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: LT.green30 }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {photos.map(p => (
              <PhotoCard key={p.id} t={t} photo={p} onReload={onReload} albums={albums} />
            ))}
          </div>
          {addingPhoto && (
            <div className="mt-4">
              <PhotoForm t={t} albumId={null}
                         onCancel={onCancelAddPhoto}
                         onSaved={() => { onCancelAddPhoto(); onReload(); }} />
            </div>
          )}
          {!addingPhoto && (
            <button onClick={onAddPhoto}
                    className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              <Plus className="h-3.5 w-3.5" /> {t('photos.admin.addPhoto')}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

/* The clip on the newsletter home · one slot, replaced here rather than
 * by a deploy. The newest active clip plays; none → the shipped one. */
const SpotlightPanel = ({ t }) => {
  const [spot, setSpot] = useState(undefined);
  const [file, setFile] = useState(null);
  const [poster, setPoster] = useState(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = () => api.newsletterSpotlight({ any: 1 })
    .then(s => { setSpot(s || null); setCaption(s?.caption || ''); })
    .catch(() => setSpot(null));
  useEffect(() => { load(); }, []);

  const dirty = !!file || !!poster || (spot ? caption !== (spot.caption || '') : caption !== '');
  const save = async () => {
    setErr(''); setBusy(true);
    try {
      if (spot) {
        await api.adminUpdateSpotlight(spot.id, { caption, video: file, poster });
      } else {
        if (!file) { setErr(t('spotlight.admin.videoRequired')); return; }
        await api.adminCreateSpotlight({ video: file, poster, caption, is_active: true });
      }
      setFile(null); setPoster(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || JSON.stringify(e?.response?.data || {}) || e.message);
    } finally { setBusy(false); }
  };
  const toggle = async () => {
    await api.adminUpdateSpotlight(spot.id, { is_active: !spot.is_active });
    load();
  };
  const remove = async () => {
    if (!confirm(t('spotlight.admin.removeConfirm'))) return;
    await api.adminDeleteSpotlight(spot.id);
    setSpot(null); setCaption('');
  };

  return (
    <section className="mb-6 rounded-2xl p-5 grid grid-cols-1 md:grid-cols-6 gap-4"
             style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <div className="md:col-span-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-[1.1rem]" style={{ color: LT.greenDeep }}>{t('spotlight.admin.title')}</div>
          <p className="text-[13px] mt-1 max-w-[560px]" style={{ color: '#3a4f44' }}>{t('spotlight.admin.intro')}</p>
        </div>
        {spot && (
          <button type="button" onClick={toggle}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium"
                  style={{ background: spot.is_active ? '#e5efe7' : 'rgba(43,65,56,0.06)',
                           color: spot.is_active ? '#3a6f4a' : LT.green50 }}>
            {spot.is_active ? <><Eye className="h-3 w-3" /> {t('spotlight.admin.active')}</>
                            : <><EyeOff className="h-3 w-3" /> {t('spotlight.admin.hidden')}</>}
          </button>
        )}
      </div>
      <div className="md:col-span-3">
        {spot === undefined && <Loading />}
        {spot === null && !file && (
          <div className="aspect-video rounded-lg flex items-center justify-center text-[12.5px] text-center px-6"
               style={{ background: 'rgba(43,65,56,0.05)', color: LT.green50 }}>
            {t('spotlight.admin.none')}
          </div>
        )}
        {(spot || file) && (
          <video key={file ? file.name : spot.video}
                 src={file ? URL.createObjectURL(file) : spot.video}
                 poster={!file && spot?.poster ? spot.poster : undefined}
                 controls muted preload="metadata" playsInline
                 className="w-full aspect-video rounded-lg bg-[#1c2a23]" />
        )}
      </div>
      <div className="md:col-span-3 flex flex-col gap-3">
        <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer self-start"
               style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
          <ImagePlus className="h-3.5 w-3.5" />
          {file ? file.name : (spot ? t('spotlight.admin.replace') : t('spotlight.admin.upload'))}
          <input type="file" accept="video/mp4,video/webm" className="hidden"
                 onChange={e => setFile(e.target.files[0] || null)} />
        </label>
        <div className="text-[11px]" style={{ color: LT.green50 }}>{t('spotlight.admin.hint')}</div>
        <Field t={t} label={t('spotlight.admin.poster')}>
          <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer self-start"
                 style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
            <ImagePlus className="h-3.5 w-3.5" />
            {poster ? poster.name : (spot?.poster ? t('photos.admin.replaceCta') : t('photos.admin.uploadCta'))}
            <input type="file" accept="image/*" className="hidden"
                   onChange={e => setPoster(e.target.files[0] || null)} />
          </label>
        </Field>
        <Field t={t} label={t('spotlight.admin.caption')}>
          <input value={caption} onChange={e => setCaption(e.target.value)} className="ph-input" maxLength={160} />
        </Field>
        {err && (
          <div className="text-[12.5px] flex items-center gap-2" style={{ color: '#b54338' }}>
            <AlertCircle className="h-3.5 w-3.5" /> {err}
          </div>
        )}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={save} disabled={busy || !dirty}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium disabled:opacity-50"
                  style={{ background: LT.greenDeep, color: '#fefefe' }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t('photos.admin.save')}
          </button>
          {spot && (
            <button type="button" onClick={remove}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                    style={{ color: '#b54338' }}>
              <Trash2 className="h-3.5 w-3.5" /> {t('spotlight.admin.remove')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const PhotoCard = ({ t, photo, onReload, albums }) => {
  const { intlLocale } = useLocale();
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const toggleVisibility = async (e) => {
    e.stopPropagation();
    setToggling(true);
    try {
      await api.adminUpdatePhoto(photo.id, { show_on_splash: !photo.show_on_splash });
      onReload();
    } finally { setToggling(false); }
  };

  if (editing) {
    return (
      <PhotoForm t={t} photo={photo} albumId={photo.album} albums={albums}
                 onCancel={() => setEditing(false)}
                 onSaved={() => { setEditing(false); onReload(); }}
                 onDeleted={() => { setEditing(false); onReload(); }} />
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden flex flex-col relative group"
         style={{ background: '#fff', border: `1px solid ${LT.green30}` }}>
      <button type="button" onClick={() => setEditing(true)}
              className="block aspect-square overflow-hidden bg-[#1c2a23] relative">
        <img src={photo.image} alt={photo.caption || ''}
             className="w-full h-full object-cover transition"
             style={{ opacity: photo.show_on_splash ? 1 : 0.5 }}
             draggable={false} />
      </button>
      {/* Inline visibility toggle · top-right overlay so HR can flip a
          photo's homepage visibility without opening the edit form. */}
      <button type="button" onClick={toggleVisibility} disabled={toggling}
              title={photo.show_on_splash ? t('photos.admin.photoVisible') : t('photos.admin.photoHidden')}
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10.5px] font-num uppercase tracking-[0.1em] font-medium transition hover:scale-[1.02]"
              style={{
                background: photo.show_on_splash ? 'rgba(229,239,231,0.92)' : 'rgba(43,65,56,0.78)',
                color: photo.show_on_splash ? '#3a6f4a' : '#fefefe',
                backdropFilter: 'blur(4px)',
              }}>
        {photo.show_on_splash
          ? <Eye className="h-3 w-3" />
          : <EyeOff className="h-3 w-3" />}
        {photo.show_on_splash ? t('photos.admin.photoVisible') : t('photos.admin.photoHidden')}
      </button>
      <div className="p-3 flex flex-col gap-1.5">
        <div className="font-display text-[14px] leading-tight truncate" style={{ color: LT.greenDeep }}>
          {photo.caption || <em style={{ color: LT.green50 }}>{t('photos.admin.noCaption')}</em>}
        </div>
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.12em] font-num"
             style={{ color: LT.green50 }}>
          {photo.taken_at && (
            <span>{new Intl.DateTimeFormat(intlLocale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(photo.taken_at))}</span>
          )}
        </div>
        <button type="button" onClick={() => setEditing(true)}
                className="mt-1 text-[12px] text-left font-medium underline-offset-2 hover:underline"
                style={{ color: LT.greenDeep }}>
          {t('photos.admin.edit')}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Album form · used for create and edit.
 * ──────────────────────────────────────────────────────────────── */
const AlbumForm = ({ t, album = null, onCancel, onSaved }) => {
  const [draft, setDraft] = useState(() => album
    ? { name: album.name, description: album.description || '', is_active: album.is_active, sort_order: album.sort_order }
    : { name: '', description: '', is_active: true, sort_order: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async (e) => {
    e?.preventDefault?.();
    setErr(''); setBusy(true);
    try {
      if (album) await api.adminUpdateAlbum(album.id, draft);
      else       await api.adminCreateAlbum(draft);
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || JSON.stringify(e2?.response?.data || {}) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save}
          className="rounded-2xl p-5 mb-5 grid grid-cols-1 md:grid-cols-6 gap-3"
          style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <Field t={t} label={t('photos.admin.field.name')} required className="md:col-span-3">
        <input required value={draft.name}
               onChange={e => setDraft({ ...draft, name: e.target.value })}
               className="ph-input" />
      </Field>
      <Field t={t} label={t('photos.admin.field.sortOrder')} hint="Plus petit = en premier" className="md:col-span-1">
        <input type="number" value={draft.sort_order}
               onChange={e => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })}
               className="ph-input" />
      </Field>
      <Field t={t} label={t('photos.admin.albumActive')} className="md:col-span-2">
        <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer mt-1.5"
               style={{ color: LT.greenDeep }}>
          <input type="checkbox" checked={!!draft.is_active}
                 onChange={e => setDraft({ ...draft, is_active: e.target.checked })} />
          {draft.is_active ? t('photos.admin.albumActive') : t('photos.admin.albumHidden')}
        </label>
      </Field>
      <Field t={t} label={t('photos.admin.field.description')} className="md:col-span-6">
        <textarea rows={2} value={draft.description}
                  onChange={e => setDraft({ ...draft, description: e.target.value })}
                  className="ph-textarea" />
      </Field>

      {err && (
        <div className="md:col-span-6 text-[12.5px] flex items-center gap-2" style={{ color: '#b54338' }}>
          <AlertCircle className="h-3.5 w-3.5" /> {err}
        </div>
      )}

      <div className="md:col-span-6 flex items-center gap-3 pt-1">
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {album ? t('photos.admin.save') : t('photos.admin.create')}
        </button>
        <button type="button" onClick={onCancel}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px]"
                style={{ color: LT.green50 }}>
          <X className="h-3.5 w-3.5" /> {t('photos.admin.cancel')}
        </button>
      </div>
    </form>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Photo form · used for create + edit. `albumId` defaults the
 * dropdown when creating from within an album section.
 * ──────────────────────────────────────────────────────────────── */
const blank = (albumId) => ({
  image: null, caption: '', album: albumId ?? null,
  episode_slug: '', taken_at: '',
  show_on_splash: true, sort_order: 0,
});

const PhotoForm = ({ t, photo = null, albumId = null, albums = [], onCancel, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(() => photo
    ? { ...photo, taken_at: photo.taken_at || '', album: photo.album || null }
    : blank(albumId));
  const [albumsLoaded, setAlbumsLoaded] = useState(albums);
  const [episodes, setEpisodes] = useState(null);
  useEffect(() => {
    api.adminNewsletters()
      .then(rows => setEpisodes(Array.isArray(rows) ? rows : (rows?.results || [])))
      .catch(() => setEpisodes([]));
  }, []);
  const [imageFile, setImageFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (albumsLoaded?.length) return;
    api.newsletterAlbums()
      .then(rows => setAlbumsLoaded(Array.isArray(rows) ? rows : (rows?.results || [])))
      .catch(() => setAlbumsLoaded([]));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e?.preventDefault?.();
    setErr(''); setBusy(true);
    try {
      const payload = { ...draft };
      if (!payload.taken_at) payload.taken_at = null;
      if (payload.album === '' || payload.album === undefined) payload.album = null;
      if (photo) {
        delete payload.image;
        if (imageFile) payload.image = imageFile;
        await api.adminUpdatePhoto(photo.id, payload);
      } else {
        if (!imageFile) { setErr(t('photos.admin.imageRequired')); return; }
        payload.image = imageFile;
        await api.adminCreatePhoto(payload);
      }
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || JSON.stringify(e2?.response?.data || {}) || e2.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!photo || !confirm(t('photos.admin.deletePhotoConfirm'))) return;
    await api.adminDeletePhoto(photo.id);
    onDeleted?.();
  };

  const previewSrc = imageFile ? URL.createObjectURL(imageFile) : (photo?.image || null);

  return (
    <form onSubmit={save}
          className="rounded-2xl p-5 grid grid-cols-1 md:grid-cols-6 gap-3"
          style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <div className="md:col-span-2 flex flex-col gap-2">
        <span className="text-[11px] uppercase tracking-[0.12em] font-num font-medium"
              style={{ color: LT.green50 }}>
          {t('photos.admin.field.image')}{!photo && <span style={{ color: '#b54338' }}> *</span>}
        </span>
        {previewSrc && (
          <div className="w-full aspect-square rounded-lg overflow-hidden bg-[#1c2a23]">
            <img src={previewSrc} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer self-start"
               style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
          <ImagePlus className="h-3.5 w-3.5" />
          {imageFile ? imageFile.name : (photo ? t('photos.admin.replaceCta') : t('photos.admin.uploadCta'))}
          <input type="file" accept="image/*" className="hidden"
                 onChange={e => setImageFile(e.target.files[0] || null)} />
        </label>
      </div>

      <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field t={t} label={t('photos.admin.field.caption')} className="md:col-span-2">
          <input value={draft.caption}
                 onChange={e => setDraft({ ...draft, caption: e.target.value })}
                 className="ph-input" maxLength={160} />
        </Field>
        <Field t={t} label={t('photos.admin.field.album')} className="md:col-span-1">
          <select value={draft.album ?? ''}
                  onChange={e => setDraft({ ...draft, album: e.target.value ? parseInt(e.target.value, 10) : null })}
                  className="ph-input">
            <option value="">— {t('photos.admin.unassigned')} —</option>
            {(albumsLoaded || []).map(al => (
              <option key={al.id} value={al.id}>{al.name}</option>
            ))}
          </select>
        </Field>
        {/* Pin to an episode from a list · a typed slug was one typo away
            from a photo that never showed. */}
        <Field t={t} label={t('photos.admin.field.episode')} hint={t('photos.admin.field.episode.hint')} className="md:col-span-1">
          <select value={draft.episode_slug || ''}
                  onChange={e => setDraft({ ...draft, episode_slug: e.target.value })}
                  className="ph-input">
            <option value="">— {t('photos.admin.field.episode.none')} —</option>
            {(episodes || []).map(ep => (
              <option key={ep.slug} value={ep.slug}>{t('nl.list.epShort')}{ep.number} · {ep.title}</option>
            ))}
            {draft.episode_slug && !(episodes || []).some(ep => ep.slug === draft.episode_slug) && (
              <option value={draft.episode_slug}>{draft.episode_slug}</option>
            )}
          </select>
        </Field>
        <Field t={t} label={t('photos.admin.field.takenAt')} className="md:col-span-1">
          <input type="date" value={draft.taken_at}
                 onChange={e => setDraft({ ...draft, taken_at: e.target.value })}
                 className="ph-input" />
        </Field>
        <Field t={t} label={t('photos.admin.field.sortOrder')} className="md:col-span-1">
          <input type="number" value={draft.sort_order}
                 onChange={e => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })}
                 className="ph-input" />
        </Field>
        <Field t={t} label={t('photos.admin.photoVisible')} className="md:col-span-2">
          <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer mt-1.5"
                 style={{ color: LT.greenDeep }}>
            <input type="checkbox" checked={!!draft.show_on_splash}
                   onChange={e => setDraft({ ...draft, show_on_splash: e.target.checked })} />
            {t('photos.admin.photoVisible')}
          </label>
        </Field>

        {err && (
          <div className="md:col-span-2 text-[12.5px] flex items-center gap-2" style={{ color: '#b54338' }}>
            <AlertCircle className="h-3.5 w-3.5" /> {err}
          </div>
        )}

        <div className="md:col-span-2 flex items-center gap-3 pt-1">
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium"
                  style={{ background: LT.greenDeep, color: '#fefefe' }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {photo ? t('photos.admin.save') : t('photos.admin.uploadCta')}
          </button>
          {photo && (
            <button type="button" onClick={remove}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                    style={{ color: '#b54338' }}>
              <Trash2 className="h-3.5 w-3.5" /> {t('photos.admin.delete')}
            </button>
          )}
          <button type="button" onClick={onCancel}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px]"
                  style={{ color: LT.green50 }}>
            <X className="h-3.5 w-3.5" /> {t('photos.admin.cancel')}
          </button>
        </div>
      </div>
    </form>
  );
};

const Field = ({ label, hint, children, required, className = '' }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-[11px] uppercase tracking-[0.12em] font-num font-medium"
          style={{ color: LT.green50 }}>
      {label}{required && <span style={{ color: '#b54338' }}> *</span>}
    </span>
    {children}
    {hint && <span className="text-[11px]" style={{ color: LT.green50 }}>{hint}</span>}
  </label>
);
